import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { replace } from 'connected-react-router'
import { useLocation } from 'react-router'

import { setSearchParamValue } from 'lib/url/searchUrlUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import { getCruiseSearchStateFromParams, getCruiseOfferFiltersFromParams } from 'lib/cruises/cruiseUtils'

import { useGlobalCruisesSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalCruisesSearch'
import useCruiseSearchFlashOffers from 'hooks/Cruise/useCruiseSearchFlashOffers'
import useCruiseSearchCount from 'hooks/Cruise/useCruiseSearchCount'
import { useAppDispatch } from 'hooks/reduxHooks'
import useQueryParams from 'hooks/useQueryParams'

import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import { GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'

import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import OfferList, { OfferListAction, OfferListAndAction, OfferListV2Item } from 'components/OfferList/OfferList'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'

import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import TextLink from 'components/Luxkit/TextLink'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'

import CruiseSearchFiltersBar from './Filters/CruiseSearchFiltersBar'
import CruiseSearchFiltersSidebar from './Filters/CruiseSearchExtraFiltersSidebar'
import CruiseSearchMobileChipFilters from './Filters/CruiseSearchMobileChipFilters'
import CruiseSearchFiltersMobile from './Filters/CruiseSearchFiltersMobile'
import CruiseSortDropdown from './Filters/Inputs/CruiseSortDropdown'
import CruiseHeadData from '../Common/CruiseHeadData'
import CruisesContact from '../Common/CruisesContact'
import { getListName } from 'analytics/enhanced-ecommerce'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { CRUISE_UNIFIED_FILTERS } from 'constants/cruise'
import cn from 'clsx'
import ContactContext from 'contexts/contactContext'
import TextButton from 'components/Luxkit/Button/TextButton'
import LinePhoneIcon from 'components/Luxkit/Icons/line/LinePhoneIcon'

const PageContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  padding: ${rem(20)} 0;

  ${mediaQueryUp.desktop} {
    padding: ${rem(16)} 0;
    gap: ${rem(32)};
    grid-template-columns: ${rem(250)} 1fr;
    &.no-filters {
      grid-template-columns: 1fr;
    }
  }

`
const MobileButton = styled(TextButton)`
  width: 100%;
  ${mediaQueryUp.tablet} {
    display: none;
  }
`
const CallText = styled.div`
  display: none;
  ${mediaQueryUp.tablet} {
    display: inline;
  }
`

const CruiseSearchPanel = styled.div`
  position: relative;
  padding: ${rem(20)} 0;
`

function CruisesSearchPage() {
  const queryParams = useQueryParams()
  const dispatch = useAppDispatch()
  const { search } = useLocation()
  const currentContact = useContext(ContactContext)
  const localContact = currentContact.local

  const tracking = useOfferListTracking(
    getListName('OfferList', 'CRUISE', 'Search Page Offer List'),
    { ias: { list: 'cruises-search' } },
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialState = useMemo(() => getCruiseSearchStateFromParams(queryParams), [])
  const { globalCruisesSearchDispatch, globalCruisesSearchState } = useGlobalCruisesSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    ...initialState,
  })

  const [filters, filtersSelected] = useMemo(() => getCruiseOfferFiltersFromParams(queryParams), [queryParams])
  const flashOffers = useCruiseSearchFlashOffers({ filters, globalFilters: globalCruisesSearchState })
  const [searchCount, fetchingSearchCount] = useCruiseSearchCount(filters)

  const interstitials = useMemo((): Array<OfferListV2Item> => ([{
    id: 'heading',
    position: 'start',
    show: 'hasOffers',
  }, {
    id: 'empty',
    position: 'start',
    show: 'hasNoOffers',
    element: !flashOffers.offerCount && <Group direction="vertical" gap={32}>
      <Group direction="vertical" gap={8}>
        <Heading variant="heading2">
          Sorry, we couldn't find any cruises
        </Heading>
        <BodyText variant="large">
          Didn&apos;t find what you were looking for?
          <CallText>
            {' '}Speak to one of our travel experts{' '}
            <TextLink href={`tel:${localContact.number}`} rel="nofollow">
              {localContact.humanReadable}
            </TextLink>
          </CallText>
        </BodyText>
        <MobileButton
          startIcon={<LinePhoneIcon />}
          fit="mobile-full-width"
          kind="secondary"
          href={`tel:${localContact.number}`}
          rel="nofollow"
        >
          Speak to a travel expert
        </MobileButton>
      </Group>
      <Group direction="vertical" gap={16}>
        <Heading variant="heading2">
          Our top <i>exclusive</i> offers
        </Heading>
        <OfferList filters={CRUISE_UNIFIED_FILTERS} tileStyle="search" />
      </Group>
    </Group>,
  }]), [flashOffers.offerCount, localContact])

  const onSortChange = useCallback((nextSort) => {
    const nextSearch = setSearchParamValue(search, 'sortBy', nextSort)
    dispatch(replace({ search: nextSearch }))
  }, [dispatch, search])

  const overrideFlashOfferList = useMemo<OfferListAndAction>(() => ({
    action: OfferListAction.OVERRIDE,
    offerList: flashOffers,
  }), [flashOffers])

  return (
    <AnalyticsPageContext.Provider value={AnalyticsPage.search} >
      <div>
        <CruiseHeadData />

        <GlobalSearchContextProvider
          state={globalCruisesSearchState}
          dispatch={globalCruisesSearchDispatch}
        >
          <CSSBreakpoint as={Pane} min="tablet">
            <LayoutContainer>
              <CruiseSearchPanel>
                <CruiseSearchFiltersBar />
              </CruiseSearchPanel>
            </LayoutContainer>
          </CSSBreakpoint>

          {(fetchingSearchCount || !!searchCount || !!flashOffers.offerCount || filtersSelected) &&
            <CSSBreakpoint max="mobile">
              <CruiseSearchFiltersMobile filters={filters} />
            </CSSBreakpoint>
          }

          <LayoutContainer>
            <PageContent className={cn({ 'no-filters': !fetchingSearchCount && !searchCount && !flashOffers.offerCount && !filtersSelected })}>
              {(fetchingSearchCount || !!searchCount || !!flashOffers.offerCount || filtersSelected) &&
                <CSSBreakpoint min="desktop">
                  <CruiseSearchFiltersSidebar filters={filters} />
                </CSSBreakpoint>
              }
              <Group direction="vertical" gap={16}>

                {(fetchingSearchCount || !!searchCount || filtersSelected) && <>
                  <Group direction="horizontal" gap={8} horizontalAlign="space-between">
                    <Group direction="horizontal" gap={16} verticalAlign="center">
                      <BodyText variant="large">
                        {fetchingSearchCount && 'Searching for cruises'}
                        {!fetchingSearchCount && `${pluralizeToString('cruise', searchCount + flashOffers.offerCount)} found`}
                      </BodyText>
                      {fetchingSearchCount && <LoadingAnimation size={8} />}
                    </Group>

                    <CSSBreakpoint min="desktop" >
                      <CruiseSortDropdown sort={filters.sortBy} onChange={onSortChange} />
                    </CSSBreakpoint>
                  </Group>
                  <CruiseSearchMobileChipFilters filters={filters} />
                </>}

                <OfferListEventsProvider tracking={tracking}>
                  <Group direction="vertical" gap={16}>
                    <OfferList
                      extraOfferListAndAction={overrideFlashOfferList}
                      tileStyle="search"
                    />

                    <OfferList
                      filters={filters}
                      tileStyle="search"
                      interstitials={interstitials}
                    />
                  </Group>
                </OfferListEventsProvider>
              </Group>
            </PageContent>
          </LayoutContainer>
          <CruisesContact />
        </GlobalSearchContextProvider>
      </div>
    </AnalyticsPageContext.Provider>
  )
}

export default CruisesSearchPage
